import { Block } from '@sanity/types/dist/dts';
import BlockContent from '@sanity/block-content-to-react';
import Link from 'next/link';
import Image from 'next/image';

import { ContentType } from '@/common/constants/ContentTypes';
import {
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  Paragrahp,
  UL,
  OL,
  ListItem,
  BlockRender,
  Anchor,
} from './styles';

function blockRender(props: any): any {
  const { style = 'normal' } = props.node;

  if (style === 'h1') return <H1>{props.children}</H1>;
  if (style === 'h2') return <H2>{props.children}</H2>;
  if (style === 'h3') return <H3>{props.children}</H3>;
  if (style === 'h4') return <H4>{props.children}</H4>;
  if (style === 'h5') return <H5>{props.children}</H5>;
  if (style === 'h6') return <H6>{props.children}</H6>;
  if (style === 'normal') return <Paragrahp>{props.children}</Paragrahp>;

  return BlockContent.defaultSerializers.types.block(props);
}
function imageRender(props) {
  const { node } = props;
  const { asset } = node;
  const { url, metadata }: { url: string; metadata: any } = asset ?? {};
  const { dimensions, lqip } = metadata ?? {};
  if (!url || !metadata) return null;
  const loader = ({ src }) => `${src}`;

  return (
    url && (
      <div
        style={{
          background: `url("${lqip}")`,
          backgroundSize: 'cover',
        }}
      >
        <Image
          src={url}
          loader={loader}
          height={dimensions.height}
          width={dimensions.width}
          objectFit="scale-down"
        />
      </div>
    )
  );
}
function listRender(props: any): JSX.Element {
  const { type, children } = props;
  if (type === 'bullet') return <UL>{children}</UL>;
  if (type === 'number') return <OL>{children}</OL>;
  return BlockContent.defaultSerializers.list(props);
}

function listItemRender({ children }: any): any {
  return <ListItem>{children}</ListItem>;
}

function linkRender(props) {
  return (
    <Link href={props.mark?.href || '#'}>
      <a target={props.mark?.href ? '_blank' : ''} rel="nofollow">
        <Anchor>{props.children}</Anchor>
      </a>
    </Link>
  );
}

function productRender(props) {
  const { node } = props || {
    node: {
      productWithVariant: {
        product: { store: { slug: null, title: null } },
      },
    },
  };

  const { slug, title } = node.productWithVariant.product.store;

  return (
    <Link href={`/${ContentType.PRODUCTS}/${slug.current}`}>
      <a target="_blank" rel="nofollow">
        <Anchor>{title}</Anchor>
      </a>
    </Link>
  );
}

function blockResourceRender(props) {
  const { node } = props || { node: {} };

  const { slug, title } = node.resourceInBody || {
    slug: null,
    title: null,
    image: {
      url: null,
      alt: null,
    },
    resourceTypes: {
      iconUrl: null,
      seeLessText: null,
      seeMoreText: null,
      type: null,
    },
  };

  return (
    <div>
      <Link href={`/${ContentType.RESOURCES}/${slug}`}>
        <Anchor>{title}</Anchor>
      </Link>
    </div>
  );
}

const serializers = {
  types: {
    block: blockRender,
    blockProduct: productRender,
    blockResource: blockResourceRender,
    image: imageRender,
    poster: imageRender,
  },
  list: listRender,
  listItem: listItemRender,
  marks: { link: linkRender },
};

export interface RenderSanityBlockProps {
  blocks: Array<Block>;
  className?: string;
}
export function RenderSanityBlock({
  blocks,
  className,
}: RenderSanityBlockProps): JSX.Element {
  return blocks?.length ? (
    <BlockRender
      className={className}
      blocks={blocks}
      serializers={serializers}
    />
  ) : null;
}

RenderSanityBlock.defaultProps = {
  className: '',
};
